import { useState, useEffect, useMemo, type JSX } from 'react'
import { Column } from 'primereact/column'
import {
  Shield, Plus, Search, RefreshCw, Pencil, Trash2,
  KeyRound, Users, CheckCircle2,
} from 'lucide-react'
import { useToast } from '../../../components/system/ToastProvider.js'
import { RoleForm } from '../../../components/rbac/RoleForm.js'
import { useRbacApi } from '../../../components/rbac/useRbacApi.js'
import { api } from '../../../api/client.js'
import type { Role, Permission, CreateRoleInput, UpdateRoleInput } from '../../../components/rbac/types.js'
import DataTable from '../../../components/ui/DataTable.js'
import { formatInTimezone } from '../../../utils/timezone.js'

/** ActionCell manages its own confirmation state so PrimeReact DataTable memoization
 *  does not block the UI from re-rendering when the user clicks delete. */
function ActionCell({ role, onEdit, onDelete }: {
  role: Role
  onEdit: (r: Role) => void
  onDelete: (r: Role) => void
}): JSX.Element {
  const [confirming, setConfirming] = useState(false)

  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-red-600 font-medium">Delete?</span>
        <button
          type="button"
          onClick={() => { onDelete(role); setConfirming(false) }}
          className="px-2 py-1 text-[10px] text-white bg-red-500 rounded-sm hover:bg-red-600"
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="px-2 py-1 text-[10px] text-gray-600 bg-gray-100 rounded-sm hover:bg-gray-200"
        >
          No
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onEdit(role)}
        className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-[#3454cf] transition-all"
        title="Edit"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={(role.userCount ?? 0) > 0}
        className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-transparent hover:text-white hover:bg-red-500 transition-all disabled:opacity-30"
        title={(role.userCount ?? 0) > 0 ? 'Cannot delete: role has users' : 'Delete'}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function formatDate(iso: string) {
  return formatInTimezone(iso, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US')
}

function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Shield
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

export default function PlatformRolesPage(): JSX.Element {
  const toast = useToast()
  const { listRoles, createRole, updateRole, deleteRole, listPermissions, loading } = useRbacApi({
    baseUrl: '/platform/rbac',
  })

  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Record<string, Permission[]>>({})
  const [dialogVisible, setDialogVisible] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadRoles()
    loadPermissions()
  }, [])

  const loadRoles = async () => {
    try {
      const data = await listRoles()
      setRoles(data.roles)
    } catch {
      toast.error('Error', 'Failed to load roles')
    }
  }

  const loadPermissions = async () => {
    try {
      const data = await listPermissions()
      setPermissions(data.permissions)
    } catch {
      toast.error('Error', 'Failed to load permissions')
    }
  }

  const openCreateDialog = () => {
    setDialogMode('create')
    setSelectedRole(null)
    setDialogVisible(true)
  }

  const openEditDialog = async (role: Role) => {
    setDialogMode('edit')
    try {
      const response = await api.get<{ role: Role }>(`/platform/rbac/roles/${role.id}`)
      setSelectedRole(response.role)
    } catch {
      toast.error('Error', 'Failed to load role details')
      setSelectedRole(role)
    }
    setDialogVisible(true)
  }

  const handleSave = async (data: CreateRoleInput | UpdateRoleInput) => {
    try {
      setFormLoading(true)
      if (dialogMode === 'create') {
        await createRole(data as CreateRoleInput)
        toast.success('Success', 'Role created')
      } else if (selectedRole) {
        await updateRole(selectedRole.id, data as UpdateRoleInput)
        toast.success('Success', 'Role updated')
      }
      setDialogVisible(false)
      loadRoles()
    } catch (err: any) {
      toast.error('Error', err.message || 'Failed to save role')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (role: Role) => {
    try {
      await deleteRole(role.id)
      toast.success('Success', 'Role deleted')
      loadRoles()
    } catch (err: any) {
      console.error('[RoleDelete]', err)
      toast.error('Error', err.message || 'Failed to delete role')
    }
  }

  const permissionOptions = Object.entries(permissions).flatMap(([domain, perms]) =>
    perms.map((p) => ({ ...p, domain }))
  )

  const filteredRoles = useMemo(() =>
    roles.filter((r) =>
      !searchQuery ||
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.description || '').toLowerCase().includes(searchQuery.toLowerCase()),
    ), [roles, searchQuery])

  const totalPerms = useMemo(() => permissionOptions.length, [permissionOptions])

  return (
    <div className="w-full space-y-6 px-4 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Platform Roles</h1>
          <p className="text-sm text-gray-400 mt-1">Manage platform-level roles and their permissions.</p>
        </div>
        <button
          onClick={openCreateDialog}
          className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
        >
          <Plus className="h-4 w-4" />
          New Role
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Roles" value={roles.length} icon={Shield} accent="#3454cf" />
        <StatCard label="Total Permissions" value={totalPerms} icon={KeyRound} accent="#10b981" />
        <StatCard label="Active Roles" value={roles.filter(r => r.userCount && r.userCount > 0).length} icon={CheckCircle2} accent="#f6931c" />
        <StatCard label="Total Users" value={roles.reduce((acc, r) => acc + (r.userCount ?? 0), 0)} icon={Users} accent="#8b5cf6" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search roles..."
            className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 font-medium">{filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''}</span>
          <div className="w-px h-4 bg-gray-200" />
          <button
            onClick={() => { setSearchQuery(''); void loadRoles() }}
            className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        value={filteredRoles}
        loading={loading}
        className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
        emptyMessage="No roles found."
      >
        <Column
          header="Role"
          body={(role: Role) => (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 text-[#3454cf]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{role.name}</p>
                {role.description && <p className="text-xs text-gray-300 truncate max-w-[200px]">{role.description}</p>}
              </div>
            </div>
          )}
          style={{ width: '30%' }}
        />
        <Column
          header="Permissions"
          body={(role: Role) => (
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf]/5 px-2.5 py-1 text-[10px] font-semibold text-[#3454cf]">
              <KeyRound className="h-3 w-3" />
              {role.permissionCount ?? 0} permissions
            </span>
          )}
        />
        <Column
          header="Users"
          body={(role: Role) => (
            <span className="text-sm font-medium text-gray-700">{role.userCount ?? 0}</span>
          )}
        />
        <Column
          header="Created"
          body={(role: Role) => (
            <span className="text-xs text-gray-400">{role.createdAt ? formatDate(role.createdAt) : '—'}</span>
          )}
        />
        <Column
          header="Actions"
          body={(role: Role) => (
            <ActionCell
              role={role}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          )}
          style={{ width: '140px' }}
        />
      </DataTable>

      <RoleForm
        visible={dialogVisible}
        mode={dialogMode}
        role={selectedRole}
        permissions={permissions}
        permissionOptions={permissionOptions}
        loading={formLoading}
        onSave={handleSave}
        onCancel={() => setDialogVisible(false)}
      />
    </div>
  )
}
